import type { TextSelection, PDFFile } from '@/types'
import { logger } from '@/utils/logger'

/**
 * Handles thumbnail generation for text selections
 * Extracted from the monolithic PDFViewer class
 */
export class ThumbnailGenerator {
  private canvas: HTMLCanvasElement | null = null
  private context: CanvasRenderingContext2D | null = null
  private currentFile: PDFFile | null = null
  
  // Thumbnail generation queue to prevent concurrency issues
  private thumbnailQueue: Array<{
    selection: TextSelection,
    scale: number,
    resolve: (data: string) => void,
    reject: (error: any) => void
  }> = []
  private isProcessingThumbnails: boolean = false
  
  // Quality configuration
  private SNIPPET_QUALITY_SCALE = 8.0
  
  // Callbacks
  private onThumbnailGenerated?: (selection: TextSelection, thumbnailData: string) => void
  private onThumbnailError?: (selection: TextSelection, error: any) => void
  
  constructor(container: HTMLElement) {
    this.canvas = container.querySelector('#pdf-canvas') as HTMLCanvasElement
    if (this.canvas) {
      this.context = this.canvas.getContext('2d')
    }
    
    logger.debug('ThumbnailGenerator initialized')
  }
  
  /**
   * Set the current file for context
   */
  setCurrentFile(file: PDFFile | null): void {
    this.currentFile = file
  }
  
  /**
   * Set callback for when a thumbnail is generated
   */
  setOnThumbnailGenerated(callback: (selection: TextSelection, thumbnailData: string) => void): void {
    this.onThumbnailGenerated = callback
  }
  
  /**
   * Set callback for when thumbnail generation fails
   */
  setOnThumbnailError(callback: (selection: TextSelection, error: any) => void): void {
    this.onThumbnailError = callback
  }
  
  /**
   * Generate thumbnail for a selection
   */
  async generateThumbnail(selection: TextSelection, scale: number = 1.0): Promise<string> {
    return new Promise((resolve, reject) => {
      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        reject(new Error('Thumbnail generation timeout after 10 seconds'))
      }, 10000)
      
      // Add to queue
      this.thumbnailQueue.push({
        selection,
        scale,
        resolve: (result) => {
          clearTimeout(timeout)
          resolve(result)
        },
        reject: (error) => {
          clearTimeout(timeout)
          reject(error)
        }
      })
      
      // Process queue if not already processing
      if (!this.isProcessingThumbnails) {
        this.processThumbnailQueue()
      }
    })
  }
  
  /**
   * Generate thumbnail for multiple selections
   */
  async generateThumbnails(selections: TextSelection[]): Promise<string[]> {
    const promises = selections.map(selection => this.generateThumbnail(selection))
    return Promise.all(promises)
  }
  
  /**
   * Process the thumbnail generation queue
   */
  private async processThumbnailQueue(): Promise<void> {
    if (this.isProcessingThumbnails || this.thumbnailQueue.length === 0) {
      return
    }
    
    this.isProcessingThumbnails = true
    
    while (this.thumbnailQueue.length > 0) {
      const { selection, scale, resolve, reject } = this.thumbnailQueue.shift()!
      
      try {
        const thumbnailData = await this.createThumbnail(selection, scale)
        resolve(thumbnailData)
        
        // Notify callback
        if (this.onThumbnailGenerated) {
          this.onThumbnailGenerated(selection, thumbnailData)
        }
        
      } catch (error) {
        reject(error)
        
        // Notify callback
        if (this.onThumbnailError) {
          this.onThumbnailError(selection, error)
        }
      }
    }
    
    this.isProcessingThumbnails = false
  }
  
  /**
   * Create a thumbnail from a selection
   */
  private async createThumbnail(selection: TextSelection, scale: number): Promise<string> {
    if (!this.canvas || !this.context || !this.currentFile) {
      throw new Error('Canvas, context, or current file not available for thumbnail generation')
    }
    
    try {
      logger.debug(`Generating thumbnail for selection ${selection.id}`)
      
      // Calculate thumbnail dimensions
      const thumbnailWidth = Math.round(selection.width * this.SNIPPET_QUALITY_SCALE)
      const thumbnailHeight = Math.round(selection.height * this.SNIPPET_QUALITY_SCALE)
      
      // Create thumbnail canvas
      const thumbnailCanvas = document.createElement('canvas')
      thumbnailCanvas.width = thumbnailWidth
      thumbnailCanvas.height = thumbnailHeight
      const thumbnailContext = thumbnailCanvas.getContext('2d')
      
      if (!thumbnailContext) {
        throw new Error('Failed to get thumbnail canvas context')
      }
      
      // Set white background
      thumbnailContext.fillStyle = '#ffffff'
      thumbnailContext.fillRect(0, 0, thumbnailWidth, thumbnailHeight)
      
      // Calculate source coordinates on the main canvas
      // Selection coordinates are in PDF space, need to convert to canvas space
      // We need to multiply by the PDF scale to get canvas coordinates
      const sourceX = Math.round(selection.x * scale)
      const sourceY = Math.round(selection.y * scale)
      const sourceWidth = Math.round(selection.width * scale)
      const sourceHeight = Math.round(selection.height * scale)
      
      // Ensure we don't go outside canvas bounds
      const maxX = Math.min(sourceX + sourceWidth, this.canvas.width)
      const maxY = Math.min(sourceY + sourceHeight, this.canvas.height)
      const actualWidth = Math.max(0, maxX - sourceX)
      const actualHeight = Math.max(0, maxY - sourceY)
      
      // Debug logging
      logger.debug('Thumbnail generation debug:', {
        selection: { x: selection.x, y: selection.y, width: selection.width, height: selection.height },
        scaled: { sourceX, sourceY, sourceWidth, sourceHeight },
        canvas: { width: this.canvas.width, height: this.canvas.height },
        actual: { actualWidth, actualHeight }
      })
      
      if (actualWidth <= 0 || actualHeight <= 0) {
        logger.warn('Invalid selection dimensions, trying with scale 1.0:', {
          selection,
          sourceX, sourceY, sourceWidth, sourceHeight,
          canvasWidth: this.canvas.width, canvasHeight: this.canvas.height,
          actualWidth, actualHeight
        })
        
        // Try with scale 1.0 as fallback (selection coordinates are already in canvas space)
        const fallbackSourceX = Math.round(selection.x)
        const fallbackSourceY = Math.round(selection.y)
        const fallbackSourceWidth = Math.round(selection.width)
        const fallbackSourceHeight = Math.round(selection.height)
        
        // Additional validation: ensure selection is within canvas bounds
        const clampedX = Math.max(0, Math.min(fallbackSourceX, this.canvas.width - 1))
        const clampedY = Math.max(0, Math.min(fallbackSourceY, this.canvas.height - 1))
        const clampedWidth = Math.max(1, Math.min(fallbackSourceWidth, this.canvas.width - clampedX))
        const clampedHeight = Math.max(1, Math.min(fallbackSourceHeight, this.canvas.height - clampedY))
        
        logger.debug('Fallback coordinates after clamping:', {
          original: { x: fallbackSourceX, y: fallbackSourceY, width: fallbackSourceWidth, height: fallbackSourceHeight },
          clamped: { x: clampedX, y: clampedY, width: clampedWidth, height: clampedHeight },
          canvas: { width: this.canvas.width, height: this.canvas.height }
        })
        
        if (clampedWidth <= 0 || clampedHeight <= 0) {
          logger.warn('Invalid selection dimensions after clamping, creating placeholder thumbnail:', {
            selection,
            clampedWidth, clampedHeight,
            canvas: { width: this.canvas.width, height: this.canvas.height }
          })
          
          // Create a simple placeholder thumbnail
          thumbnailContext.fillStyle = '#f0f0f0'
          thumbnailContext.fillRect(0, 0, thumbnailWidth, thumbnailHeight)
          
          // Add text indicating large selection
          thumbnailContext.fillStyle = '#666'
          thumbnailContext.font = '12px Arial'
          thumbnailContext.textAlign = 'center'
          thumbnailContext.fillText('Large Selection', thumbnailWidth / 2, thumbnailHeight / 2)
          thumbnailContext.fillText(`${Math.round(selection.width)}×${Math.round(selection.height)}`, thumbnailWidth / 2, thumbnailHeight / 2 + 15)
        } else {
          // Additional check: if selection is too large, create a representative thumbnail
          const maxThumbnailSize = 800 // Maximum reasonable thumbnail size
          if (clampedWidth > maxThumbnailSize || clampedHeight > maxThumbnailSize) {
            logger.warn('Selection too large, creating representative thumbnail:', {
              clampedWidth, clampedHeight, maxThumbnailSize
            })
            
            // Create a representative thumbnail from the center of the selection
            const centerX = clampedX + Math.floor(clampedWidth / 2)
            const centerY = clampedY + Math.floor(clampedHeight / 2)
            const representativeSize = Math.min(maxThumbnailSize, Math.min(clampedWidth, clampedHeight))
            const halfSize = Math.floor(representativeSize / 2)
            
            const repX = Math.max(0, centerX - halfSize)
            const repY = Math.max(0, centerY - halfSize)
            const repWidth = Math.min(representativeSize, this.canvas.width - repX)
            const repHeight = Math.min(representativeSize, this.canvas.height - repY)
            
            logger.debug('Using representative thumbnail coordinates:', {
              repX, repY, repWidth, repHeight
            })
            
            // Use representative coordinates
            thumbnailContext.drawImage(
              this.canvas,
              repX, repY, repWidth, repHeight,
              0, 0, thumbnailWidth, thumbnailHeight
            )
          } else {
            // Use clamped coordinates
            thumbnailContext.drawImage(
              this.canvas,
              clampedX, clampedY, clampedWidth, clampedHeight,
              0, 0, thumbnailWidth, thumbnailHeight
            )
          }
        }
      } else {
        // Use original coordinates
        thumbnailContext.drawImage(
          this.canvas,
          sourceX, sourceY, actualWidth, actualHeight,
          0, 0, thumbnailWidth, thumbnailHeight
        )
      }
      
      // Convert to base64 data URL
      const thumbnailData = thumbnailCanvas.toDataURL('image/png', 0.9)
      
      logger.debug(`Thumbnail generated for selection ${selection.id}: ${thumbnailData.length} chars`)
      return thumbnailData
      
    } catch (error) {
      logger.error(`Failed to generate thumbnail for selection ${selection.id}:`, error)
      throw error
    }
  }
  
  /**
   * Generate a fast preview thumbnail (lower quality, faster)
   */
  async generateFastThumbnail(selection: TextSelection, scale: number = 1.0): Promise<string> {
    if (!this.canvas || !this.context) {
      throw new Error('Canvas or context not available for fast thumbnail generation')
    }
    
    try {
      // Use lower quality scale for faster generation
      const fastScale = 2.0
      const thumbnailWidth = Math.round(selection.width * fastScale)
      const thumbnailHeight = Math.round(selection.height * fastScale)
      
      // Create thumbnail canvas
      const thumbnailCanvas = document.createElement('canvas')
      thumbnailCanvas.width = thumbnailWidth
      thumbnailCanvas.height = thumbnailHeight
      const thumbnailContext = thumbnailCanvas.getContext('2d')
      
      if (!thumbnailContext) {
        throw new Error('Failed to get thumbnail canvas context')
      }
      
      // Set white background
      thumbnailContext.fillStyle = '#ffffff'
      thumbnailContext.fillRect(0, 0, thumbnailWidth, thumbnailHeight)
      
      // Calculate source coordinates (selection coordinates are in PDF space, need to convert to canvas space)
      const sourceX = Math.round(selection.x * scale)
      const sourceY = Math.round(selection.y * scale)
      const sourceWidth = Math.round(selection.width * scale)
      const sourceHeight = Math.round(selection.height * scale)
      
      // Ensure we don't go outside canvas bounds
      const maxX = Math.min(sourceX + sourceWidth, this.canvas.width)
      const maxY = Math.min(sourceY + sourceHeight, this.canvas.height)
      const actualWidth = maxX - sourceX
      const actualHeight = maxY - sourceY
      
      if (actualWidth <= 0 || actualHeight <= 0) {
        throw new Error('Invalid selection dimensions for fast thumbnail generation')
      }
      
      // Draw the selection area
      thumbnailContext.drawImage(
        this.canvas,
        sourceX, sourceY, actualWidth, actualHeight,
        0, 0, thumbnailWidth, thumbnailHeight
      )
      
      // Convert to base64 data URL with lower quality
      const thumbnailData = thumbnailCanvas.toDataURL('image/jpeg', 0.7)
      
      logger.debug(`Fast thumbnail generated for selection ${selection.id}`)
      return thumbnailData
      
    } catch (error) {
      logger.error(`Failed to generate fast thumbnail for selection ${selection.id}:`, error)
      throw error
    }
  }
  
  /**
   * Check if thumbnail generation is in progress
   */
  isGenerating(): boolean {
    return this.isProcessingThumbnails || this.thumbnailQueue.length > 0
  }
  
  /**
   * Get queue length
   */
  getQueueLength(): number {
    return this.thumbnailQueue.length
  }
  
  /**
   * Clear the thumbnail queue
   */
  clearQueue(): void {
    // Reject all pending requests
    this.thumbnailQueue.forEach(({ reject }) => {
      reject(new Error('Thumbnail generation cancelled'))
    })
    this.thumbnailQueue = []
    this.isProcessingThumbnails = false
    
    logger.debug('Thumbnail queue cleared')
  }
  
  /**
   * Set thumbnail quality scale
   */
  setQualityScale(scale: number): void {
    this.SNIPPET_QUALITY_SCALE = Math.max(1.0, Math.min(12.0, scale))
    logger.debug(`Thumbnail quality scale set to: ${this.SNIPPET_QUALITY_SCALE}`)
  }
  
  /**
   * Get current quality scale
   */
  getQualityScale(): number {
    return this.SNIPPET_QUALITY_SCALE
  }
  
  /**
   * Clear all state
   */
  clearState(): void {
    this.clearQueue()
    this.currentFile = null
  }
}
